import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDInput from "mdComponents/MDInput";
import NotificationAlert from "controls/NotificationAlert";
import locationsService from "services/locationsService";
import { formatDate } from "../../Utils";
import { useHostPortalContext } from "context/HostPortalContext";
import { useNavigationMessageContext } from "context/NavigationMessageContext";
import LoadingSpinner from "controls/LoadingSpinner";

const PoorConditionReports: React.FC = () => {
  const { id } = useParams<{ id: any }>();
  const { host } = useHostPortalContext();
  const { setMessage } = useNavigationMessageContext();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbar, setSnackbar] = useState({
    color: "success",
    content: "Saved Successfully!",
    title: "Save Successful",
    icon: "check",
    dateTime: "just now",
  });

  const openSnackBar = () => setShowSnackBar(true);
  const closeSnackBar = () => setShowSnackBar(false);

  useEffect(() => {
    setMessage(`${host?.name} Poor Condition Reports`);
  }, []);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        const { data } = await locationsService.fetchLocationPoorConditionReports(host ? host.id : id);
        setReports(data ?? []);
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, [host, id]);

  const onViewDetails = (report: any) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const onModalClose = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const filteredReports = reports.filter((r) =>
    `${r.profiles.firstName} ${r.profiles.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        {loading ? (
            <Grid container spacing={6} justifyContent="center">
                <Grid item xs={12} display="flex" justifyContent="center">
                <Card sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <LoadingSpinner title="Loading Poor Condition Reports..." size={30} />
                </Card>
                </Grid>
            </Grid>
        ) : filteredReports.length > 0 ? (
          <Grid container justifyContent="center">
            <Grid item xs={12} xl={12}>
                <MDTypography variant="h6" mb={1}>
                  Most recent 100 app user submitted poor condition reports for {host?.name ?? "this location"}
                </MDTypography>
                {/* <MDInput
                  placeholder="Search reporter..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                /> */}

                {filteredReports.map((report) => (
                  <MDBox
                    key={report.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    mb={1}
                    sx={{
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#efefef" },
                    }}
                    onClick={() => onViewDetails(report)}
                  >
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <img
                        src={report.picture}
                        alt={`${report.profiles.firstName} ${report.profiles.lastName}`}
                        width={50}
                        height={50}
                        style={{ borderRadius: "50%", objectFit: "cover" }}
                      />
                      <MDBox>
                        <MDTypography variant="h6">
                          {report.profiles.firstName} {report.profiles.lastName}
                        </MDTypography>
                        <MDTypography variant="body2">
                          Reported on {formatDate(report.GoingWorkflow.startTime, true)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                ))}
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
                <Card sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <MDBox display="flex" justifyContent="center" alignItems="center">
                        <MDTypography variant="h6">No poor condition reports found for this location.</MDTypography>
                    </MDBox>
                </Card>
            </Grid>
        </Grid>
        )}

        {/* DETAILS MODAL */}
        <Dialog open={showModal} onClose={onModalClose} fullWidth maxWidth="sm">
          <DialogTitle>Poor Condition Report</DialogTitle>
          <DialogContent>
            {selectedReport && (
              <MDBox alignItems="center" display="flex" flexDirection="column">
                <MDBox gap={2} mb={1}>
                  <MDBox
                    component="img"
                    src={selectedReport.picture}
                    sx={{
                        width: "100%",
                        maxWidth: 400,
                        borderRadius: "12px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                    }}
                    />
                </MDBox>
                  <MDBox>
                    <MDTypography variant="h6">
                      {selectedReport.profiles.firstName} {selectedReport.profiles.lastName}
                    </MDTypography>
                    <MDTypography variant="body2">
                      Reported on {formatDate(selectedReport.created_at, true)}
                    </MDTypography>
                    <MDTypography variant="body2">
                      The user reported poor conditions. Please close for cleaning until resolved.
                    </MDTypography>
                  </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton variant="outlined" color="secondary" onClick={onModalClose}>
              Done
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Notification */}
        <NotificationAlert
          open={showSnackBar}
          onClose={closeSnackBar}
          close={closeSnackBar}
          color={snackbar.color}
          icon={snackbar.icon}
          title={snackbar.title}
          content={snackbar.content}
          dateTime={snackbar.dateTime}
        />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default PoorConditionReports;
